import React from "react";

export const mainParagraph = (
  <div className="mainParagraph">
    <p className="content justified">
      Oswaldo Díaz Díaz fue el primer colombiano en publicar un ensayo en el que se reflexionara detenidamente y con
      suma profundidad sobre la literatura infantil. Se trata de “Aspecto de la literatura infantil”, un texto breve
      incluido en el número 26 de la <i>Revista de las Indias</i> de 1941.
    </p>
    <p className="content justified">
      Para este entonces, Don Os ya había publicado su primera antología de cuentos infantiles titulada{" "}
      <i>El país de Lilac, Rin Rin</i> y <i>Chanchito</i>.
    </p>
    <p className="content justified">
      La tradición de literatura infantil local se construía sobre el legado de Pombo, con voces como las de Víctor
      Eduardo Caro, Ecco Nelly, Luz Stella, Fanny Osorio, Simón Latino y Eduardo Caballero Calderón, quienes recreaban
      el universo literario de los niños colombianos de la década de 1940.
    </p>
  </div>
);

export const bottomParagraph = (
  <div className="bottomParagraph">
    <p className="content justified">
      En este marco, Don Os hizo lo que nadie más se había detenido a hacer: escribir un texto crítico alrededor de las
      narraciones infantiles, adelantándose décadas a teóricos e investigadores locales.
    </p>
  </div>
);

export const sections = (
  <div className="sectionsReview">
    <div className="sectionReview">
      <h3 className="review-title subtitle">Definición y clasificación de la literatura infantil según Don Os</h3>
      <p className="content justified">
        En “Aspecto de la literatura infantil” –texto que debería ser de obligada lectura para quienes trabajan con
        libros para niños–, Oswaldo Díaz Díaz aborda el tema desde tres líneas: &nbsp;
        <b>la definición de la literatura infantil, la clasificación de esta y sus características de fondo y forma.</b>
      </p>
      <p className="content justified">
        “Definir la literatura infantil, señalar todo su alcance y extensión, es empresa ardua. Así que intentaré una
        definición personal” y continúa afirmando que la literatura infantil debe determinarse, más que por la edad de
        sus lectores, por la capacidad de una persona para disfrutarla “con limpidez de espíritu”.
      </p>
      <p className="content justified">
        Esta precisión puede ser problemática: ¿A la luz de qué se define que alguien tenga esa inocencia o pulcritud
        espiritual? A pesar de esta imprecisión, deja claro que es una forma de literatura que trasciende edades y se
        centra en el disfrute.
      </p>
      <p className="content justified">
        Y sigue: la clasificación de esta materia depende de dos aspectos. El primero, que todo género literario tiene
        cabida en los libros infantiles, aunque el verso sea el que con más naturalidad conecta con los más jóvenes.
      </p>
      <p className="content justified">
        El segundo, que la literatura infantil puede abarcar diversos temas, pero estos se vinculan mejor con
        determinados lectores según la etapa en la que se encuentren.
      </p>
    </div>
    <div className="sectionReview">
      <h3 className="review-title subtitle">Fondo y forma: fantasía, imaginación, lenguaje y verosimilitud</h3>
      <p className="content justified">
        Este, a mi manera de ver, es el apartado más interesante de este escrito de Don Os. No solo es crítico, sino
        también propositivo. En él, de forma sucinta, aborda temas que se mantienen vigentes en la escritura de la
        literatura infantil.
      </p>
      <p className="content justified">
        En primer lugar, el <b>contenido moral</b>. “Nada tan inconveniente como las moralejas expresas y los modelos de
        virtud”. Por lo contrario, para este autor, la moralidad debe centrarse en el propio conocimiento, la energía y
        el entusiasmo juvenil. “Es la repulsión que inspira la mentira, el engaño, la crueldad y la cobardía […]: La
        inquietud mental que se suscite, el conocimiento de la vida, adecuado a su edad, que se le enseñe”.
      </p>
      <p className="content justified">
        Luego está <b>lo maravilloso</b> que –adelantándose treinta años a los escritos de Todorov sobre este tema– es
        distinto a lo que los adultos entienden por fantasía. Lo maravilloso, para Don Os, está atravesado por la
        experiencia del niño en el mundo: maravilloso es que germine una planta, maravillosos son los inventos de la
        humanidad, maravilloso fue un personaje como Galileo.
      </p>
      <p className="content justified">
        Posteriormente, el autor se centra en <b>la verosimilitud</b> de la literatura infantil, apelando al concepto de
        “verdad imaginada” de Walter de la Mare: aunque el niño ame la ilusión de lo inusitado, “requiere de asideros
        firmes para su imaginación […], pedestal de realidad para levantar la estatua de la fantasía”.
      </p>
    </div>
    <div className="sectionReview">
      <h3 className="review-title subtitle">Colorear con palabras</h3>
      <p className="content justified">
        De acuerdo con Don Os, una de las cualidades más relevantes de un texto infantil debe ser la de colorear con
        palabras: “el color, el brillo de los tonos, la policromía dan pie para que el{" "}
        <b>niño construya sus imágenes y levante sus propios escenarios”</b>.
      </p>
      <p className="content justified">
        Así mismo, un texto infantil debe tener un <b>ritmo</b> adecuado, en el que las descripciones sean precisas y no
        demasiado extensas. Que motiven al lector a seguir leyendo.
      </p>
      <p className="content justified">
        Por otro lado, “el <b>lenguaje para los niños</b> debe ser serio, infantil pero no pueril, inocente pero no
        ridículo. Debe escribirse esta clase de literatura infantil con sencillez, con las palabras usuales, con la
        construcción más lógica, con la más discreta elegancia, con pulcritud a toda prueba”.
      </p>
      <p className="content justified">
        Los <b>finales felices</b>, dice, no son obligatorios en los relatos infantiles. Con todo, un autor de
        literatura infantil debería dejar siempre una luz de esperanza, “revistiendo los finales desgraciados de un
        manto de serenidad”.
      </p>
    </div>
    <div className="sectionReview">
      <h3 className="review-title subtitle">Vicios de siempre en la literatura infantil</h3>
      <p className="content justified">
        Es muy llamativo reconocer cómo los vicios de la literatura infantil de hace más de cincuenta años prevalecen a
        pesar del tiempo. Para Díaz Díaz, la puerilidad en el lenguaje es quizá uno de los más extendidos.
      </p>
      <p className="content justified">
        Muchos autores “a fuerza de querer volverse niños e infantilizar su estilo caen en trivialidades insoportables,
        en recargo de diminutivos, en onomatopeyas, en trucos burdísimos”.
      </p>
      <p className="content justified">
        Y continúa: “Nada más presuntuoso y desacertado, nada que retraiga más al niño de la lectura que esos pesados
        relatos con intensión [sic] educativa, donde niños de belleza femenina y ánimo almibarado se utilizan para
        ejemplo”.
      </p>
      <p className="content justified">
        Una lectura iluminadora que pone sobre la mesa temas relevantes para la construcción y discusión de la
        literatura infantil actual.
      </p>
    </div>
  </div>
);
